/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { DropdownModel } from '../../../../../core/models';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-time-selector',
    templateUrl: './time-selector.component.html',
    styleUrls: ['./time-selector.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => TimeSelectorComponent),
        }
    ]
})
export class TimeSelectorComponent extends BaseComponent implements ControlValueAccessor, OnInit {
    /**
     * Form control name from parent.
     */
    @Input() public formControlName: string;

    /**
     * Parent's form group.
     */
    @Input() public formGroup: FormGroup;

    /**
     * ID for the time selector.
     */
    public componentId: string;

    /**
     * Dropdown to store the units of time.
     */
    public timeUnits = [
        <DropdownModel>{
            key: 's',
            value: 'seconds'
        },
        <DropdownModel>{
            key: 'm',
            value: 'minutes'
        },
        <DropdownModel>{
            key: 'h',
            value: 'hours'
        },
        <DropdownModel>{
            key: 'd',
            value: 'days'
        },
    ];

    /**
     * Form to set the duration and unit of time.
     */
    public durationForm: FormGroup;

    /**
     * Boolean for capturing the selected text event.
     * @private
     */
    private selectEvent: boolean = false;

    /**
     * Format the duration received from the parent form.
     * @param value
     * @private
     */
    private static splitFormDuration(value): any {
        const unit = value.replace(/\d/g, '');
        const duration = value.replace(/[a-z]/gi, '');

        return { duration, unit };
    }

    constructor(
        private formBuilder: FormBuilder
    ) {
        super();
    }

    public ngOnInit() {
        this.setUpForm();
        this.setUpFormSubscribers();
        this.setComponentId();
    }

    /**
     * Required function for implementation.
     */
    public writeValue(value): void {
        const { duration, unit } = TimeSelectorComponent.splitFormDuration(value);
        if (duration && unit) {
            this.setUnitAndDurationToLocalForm(duration, unit);
        }

        if (this.formGroup.disabled) {
            this.durationForm.disable();
        } else {
            this.durationForm.enable();
        }
    }

    /**
     * Required function for implementation.
     */
    public registerOnChange(): void {
    }

    /**
     * Required function for implementation.
     */
    public registerOnTouched(): void {
    }

    /**
     * Restrict number value input length.
     * @param event
     */
    public restrictValueLength(event): void {
        if (this.selectEvent) {
            this.selectEvent = false;
        } else {
            const navigationKeys = [
                'ArrowDown',
                'ArrowLeft',
                'ArrowRight',
                'ArrowUp',
                'Backspace',
                'Clear',
                'Copy',
                'Delete',
                'End',
                'Enter',
                'Escape',
                'Home',
                'Paste',
                'Tab'
            ];
            if (navigationKeys.includes(event.key)) {
                this.durationForm.get('duration').setValue(event.target.value);
            } else if (event.target.value.length >= 5) {
                this.durationForm.get('duration').setValue(event.target.value.splice(0, 4));
            }
        }

    }

    /**
     * Boolean triggered when the "select" event is triggered.
     */
    public selectEventTriggered(): void {
        this.selectEvent = true;
    }

    /**
     * Set the unit of time and the duration.
     * @param duration
     * @param unit
     * @private
     */
    private setUnitAndDurationToLocalForm(duration, unit): void {
        const formDuration = this.durationForm.get('duration');
        const formUnit = this.durationForm.get('unit');
        if (formDuration.value !== duration) {
            formDuration.setValue(duration);
        }
        if (formUnit.value !== unit) {
            formUnit.setValue(unit);
        }
    }

    /**
     * Set up the form.
     * @private
     */
    private setUpForm(): void {
        this.selectEvent = false;
        this.durationForm = this.formBuilder.group({
            duration: [null, [Validators.required]],
            unit: [null, [Validators.required]]
        });
    }

    /**
     * Subscribe to form changes
     * @private
     */
    private setUpFormSubscribers(): void {
        this.durationForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: values => {
                    this.setDurationInParentForm(values);
                }
            });
    }

    /**
     * Format the duration and output to form.
     * @private
     */
    private setDurationInParentForm({ duration, unit }): void {
        this.formGroup.get(this.formControlName).setValue(duration + unit);
    }

    /**
     * Set the component's id.
     * @private
     */
    private setComponentId() {
        this.componentId = this.formControlName + '-duration';
    }
}
